/**
 * Field. Implements DESIGN.md §Components > Inputs / Fields, "Field": a 14px/600 label,
 * 8px to the control, 16px between fields, an optional 12px muted hint below, and a
 * 14px danger error line below that. Wires the label (htmlFor/id), the hint and the
 * error (aria-describedby) and aria-invalid onto the single control it wraps.
 */
import React, { cloneElement, useId } from 'react';
import { cn } from './cn';

export function Field({ label, description, error, required = false, id, className, children }) {
  const auto = useId();
  const controlId = id || children.props.id || `field-${auto}`;
  const descId = description ? `${controlId}-description` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [children.props['aria-describedby'], descId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('tw-grid tw-gap-2 tw-mb-4 tw-min-w-0', className)}>
      <label htmlFor={controlId} className="tw-flex tw-gap-3 tw-text-sm tw-font-semibold tw-text-text">
        <span>
          {label}
          {required && (
            <span aria-hidden="true" className="tw-ml-1 tw-text-danger">*</span>
          )}
        </span>
      </label>
      {cloneElement(children, {
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? 'true' : children.props['aria-invalid'],
        'aria-required': required ? 'true' : children.props['aria-required'],
        // Composite controls (Input) take `invalid` for the danger border; DOM elements do not.
        ...(typeof children.type !== 'string' && error ? { invalid: true } : {}),
      })}
      {description && (
        <p id={descId} className="tw-text-xs tw-text-text-muted">{description}</p>
      )}
      {error && (
        <p id={errorId} className="tw-text-sm tw-text-danger">{error}</p>
      )}
    </div>
  );
}
